import React, { useCallback, useEffect, useRef } from 'react';
import XOutlined from '@uber/base-icons/x-outlined';
import { styled } from 'baseui';
import { Button, SIZE as BUTTON_SIZE } from 'baseui/button';
import { Textarea } from 'baseui/textarea';
type SearchProps = {
  onSearch: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  bottomEnhancer?: React.ReactNode;
  initialValue?: string;
};
const SearchContainer = styled('div', ({
  $theme
}) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: $theme.sizing.scale200,
  flex: 1
}));
const InputRow = styled('div', ({
  $theme
}) => ({
  display: 'flex',
  gap: $theme.sizing.scale300,
  alignItems: 'flex-end'
}));
const TextareaWrapper = styled('div', {
  position: 'relative',
  flex: '1'
});
const InlineClearButton = styled('button', ({
  $theme
}) => ({
  position: 'absolute',
  right: $theme.sizing.scale300,
  top: $theme.sizing.scale300,
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  padding: $theme.sizing.scale0,
  display: 'flex',
  alignItems: 'center',
  color: $theme.colors.contentSecondary,
  ':hover': {
    color: $theme.colors.contentPrimary
  },
  ':disabled': {
    cursor: 'not-allowed',
    opacity: 0.5
  }
}));
const MAX_ROWS = 10;
export const Search = ({
  onSearch,
  placeholder = 'Enter search term...',
  disabled = false,
  bottomEnhancer,
  initialValue
}: SearchProps) => {
  const [value, setValue] = React.useState<string>(initialValue ?? '');
  const wrapperRef = useRef<HTMLDivElement>(null);
  const adjustHeight = useCallback(() => {
    const textarea = wrapperRef.current?.querySelector('textarea');
    if (!textarea) return;
    textarea.style.height = 'auto';
    const style = window.getComputedStyle(textarea);
    const lineHeight = parseFloat(style.lineHeight) || 20;
    const paddingTop = parseFloat(style.paddingTop) || 0;
    const paddingBottom = parseFloat(style.paddingBottom) || 0;
    const maxHeight = lineHeight * MAX_ROWS + paddingTop + paddingBottom;
    const newHeight = Math.min(textarea.scrollHeight, maxHeight);
    textarea.style.height = `${newHeight}px`;
    textarea.style.overflowY = textarea.scrollHeight > maxHeight ? 'auto' : 'hidden';
  }, []);
  useEffect(() => {
    adjustHeight();
  }, [value, adjustHeight]);
  const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setValue(event.target.value);
  };
  const handleSearch = () => {
    if (!disabled) {
      onSearch(value);
    }
  };
  const handleClear = () => {
    if (!disabled) {
      setValue('');
      onSearch('');
    }
  };
  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey && !disabled) {
      event.preventDefault();
      handleSearch();
    }
  };
  return <SearchContainer>
      <InputRow>
        <TextareaWrapper ref={wrapperRef}>
          <Textarea value={value} onChange={handleChange} onKeyDown={handleKeyDown} placeholder={placeholder} disabled={disabled} rows={1} overrides={{
          Input: {
            style: {
              resize: 'none',
              overflowY: 'hidden',
              paddingRight: value ? '28px' : undefined,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word'
            }
          }
        }} />
          {value && <InlineClearButton data-testid="clear-button" onClick={handleClear} aria-label="Clear" disabled={disabled} type="button">
              <XOutlined size={14} />
            </InlineClearButton>}
        </TextareaWrapper>
        <Button onClick={handleSearch} size={BUTTON_SIZE.default} disabled={disabled}>
          Generate
        </Button>
      </InputRow>
      {bottomEnhancer}
    </SearchContainer>;
};